import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import EmailEditor, { Design, HtmlExport, SimpleMergeTag, UnlayerOptions } from 'react-email-editor';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ICreateCampaignRequest, ILocationState, TemplateOrTemplateFilter } from '../../../models';
import { IEditHtmlNewsletterRequest } from '../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IErrorMessageComponentProps,
	IFullscreenModalComponentProps,
} from '../../../models/AppState';
import { isIE11 } from '../../../models/Browser';
import { useEnvironment, useFullscreenModal } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import {
	DefaultContactPlaceholders,
	SenderEmailPlaceholder,
	SenderFirstNamePlaceholder,
	SenderFullNamePlaceholder,
	SenderPhonePlaceholder,
	Token,
} from '../../../models/Token';
import { openDownloadUrl, removeForbiddenTagsFromHTML, unlayerCustomCss } from '../../../models/UiUtils';
import { useTemplateExportPDFMutation, useTemplateScopeMutation } from '../../../queries';
import {
	asApiError,
	ContentSourceFormat,
	ITemplate,
	selectKeysOf,
	TemplateScope,
} from '../../../viewmodels/AppViewModels';
import { HtmlNewsletter, HtmlNewsletterViewModel } from '../../../viewmodels/HtmlNewletterViewModels';
import { ExportConfirmationModalV2 } from '../ExportConfirmation';
import { IModalProps, Modal } from '../Modal';
import { ShareTemplateCheckbox } from '../ShareTemplateCheckbox';
import { ShareScope } from '../ShareTemplateCheckbox/ShareTemplateCheckbox';
import { TextInputModal } from '../TextInputModal';
import { getEmailEditorFonts } from './fonts';
import {
	HtmlNewsletterEditorBodyContext,
	HtmlNewsletterEditorHeaderContext,
	IHtmlNewsletterEditorBodyContext,
	IHtmlNewsletterEditorHeaderContext,
} from './presentation';
import { SaveTemplateModal } from './SaveTemplateModal';
import { styleSheet } from './styles';

interface IProps extends IErrorMessageComponentProps, IFullscreenModalComponentProps {
	className?: string;
	newsletter: HtmlNewsletter;
	onRef?(ref?: IHtmlNewsletterEditorComponent): void;
	styles?: StyleDeclarationValue[];
	isEditModal?: boolean;
	subject: string;
}

export type ModalBehaviorProps = {
	open: boolean;
	onSuccess?: () => void;
};

export interface IHtmlNewsletterEditorComponent {
	getContent(): Promise<HtmlExport>;
}

const defaultTemplateName = 'Untitled Newsletter';

// All placeholders used in Unlayer merge tags - these need to be converted from symbols to HTML tokens on export
const UnlayerMergeTagPlaceholders = [
	...DefaultContactPlaceholders,
	SenderFirstNamePlaceholder,
	SenderFullNamePlaceholder,
	SenderEmailPlaceholder,
	SenderPhonePlaceholder,
];

/**
 * Convert merge field symbols (e.g., {{first name}}) to HTML tokens in exported HTML.
 * Unlayer inserts merge tags as plain text symbols, so we need to convert them to
 * the HTML span format that the backend expects for processing.
 */
const convertMergeFieldSymbolsToTokens = (html: string): string => {
	let result = html;
	for (const placeholder of UnlayerMergeTagPlaceholders) {
		const [converted] = Token.replaceContent(result, placeholder);
		if (converted) {
			result = converted;
		}
	}
	return result;
};

const HtmlNewsletterEditorSFC: React.FC<React.PropsWithChildren<IProps>> = props => {
	const { className, styles, children, newsletter, errorMessages, onRef, isEditModal, subject } = props;
	const history = useHistory();
	const editorRef = React.useRef<EmailEditor>(null);
	const location = useLocation();
	const fullscreen = useFullscreenModal();
	const [isLoaded, setIsLoaded] = React.useState(false);
	const logger = useEventLogging('HtmlNewsletterEditor');
	const environment = useEnvironment();
	const [shareScope, setShareScope] = React.useState<ShareScope>(() => {
		if (newsletter.template.scope) {
			return newsletter.template.scope === TemplateScope.User ? TemplateScope.User : TemplateScope.Account;
		}

		return isEditModal ? TemplateScope.Account : TemplateScope.User;
	});

	const exportPdfMutation = useTemplateExportPDFMutation();
	const updateScopeMutation = useTemplateScopeMutation({
		onSuccess: () => {
			toast.success('Template scope updated!');
		},
		onError: error => {
			const apiError = asApiError(error);
			logger.logApiError('UpdateTemplateScope-Error', apiError);
			errorMessages.pushApiError(apiError);
		},
	});

	const onEditorLoaded = React.useCallback(
		(emailEditor?: EmailEditor) => {
			editorRef.current = emailEditor;
			if (!!emailEditor && isLoaded !== true) {
				setIsLoaded(true);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const reloadTemplate = React.useCallback(
		(savedTemplate: ITemplate) => {
			try {
				const newDesign: Design = JSON.parse(savedTemplate.content.source);
				editorRef.current?.editor.loadDesign(newDesign);
			} catch (error) {
				logger.logApiError('TemplateSave-Error', error);
			}
		},
		[logger]
	);

	const notifyHtmlForbiddenTagError = React.useCallback((errors: Record<string, boolean>) => {
		const errorKeys = Object.keys(errors);

		errorKeys.forEach(key => {
			toast.error(`Your template had a tag ${key} in it, for security reasons, we removed from the saved version`);
		});
	}, []);

	const getCurrentTemplateRepresentation = React.useCallback(() => {
		return new Promise<ITemplate | null>(resolve => {
			const getBaseTemplate = () => {
				const result = selectKeysOf({ content: {}, name: defaultTemplateName, ...newsletter.template }, [
					'content',
					'templateType',
					'subject',
					'name',
					'id',
					'templateType',
					'scope',
				]) as PartiallyRequired<ITemplate, 'content'>;
				if (result.id === 'new') {
					delete result.id;
				}
				return result;
			};
			if (!editorRef.current) {
				if (!newsletter.canEdit) {
					return resolve(getBaseTemplate());
				}
				return resolve(null);
			}
			editorRef.current.editor.exportHtml(async exported => {
				const template = getBaseTemplate();
				// remove doctype line
				const document = HtmlNewsletterViewModel.removeDoctypeFromHtmlStringValue(exported.html);
				const { html: htmldocument, errors } = removeForbiddenTagsFromHTML(document);
				notifyHtmlForbiddenTagError(errors);
				// Convert merge field symbols to HTML tokens for backend processing
				template.content.document = convertMergeFieldSymbolsToTokens(htmldocument);
				template.content.source = JSON.stringify(exported.design);
				template.content.sourceFormat = ContentSourceFormat.UnlayerHtmlNewsletter;
				template.subject = subject;
				resolve(template);
			});
		});
	}, [newsletter, editorRef, subject, notifyHtmlForbiddenTagError]);

	const replaceHistoryAfterSavingNewTemplate = React.useCallback(
		(savedTemplate: ITemplate) => {
			const locationState: ILocationState<any, IEditHtmlNewsletterRequest> = location?.state ?? {};
			if (!locationState.model && environment.appType === 'admin') {
				return;
			}
			if (locationState.model?.impersonationContext?.account?.id) {
				fullscreen?.history?.replace({
					pathname: `/accounts/${locationState?.model?.impersonationContext?.account?.id}/htmlnewsletter/${savedTemplate.id}`,
					state: locationState,
				});
			} else {
				history.replace({
					pathname: `/campaigns/htmlnewsletter/${savedTemplate.id}`,
				});
			}
		},
		[environment.appType, fullscreen?.history, history, location?.state]
	);

	const onAutoSave = React.useCallback(async () => {
		try {
			const template = await getCurrentTemplateRepresentation();
			return await newsletter.save(template);
		} catch (err) {
			const apiError = asApiError(err);
			logger.logApiError('TemplateAutoSave-Error', apiError);
			errorMessages.pushApiError(apiError);
			return null;
		}
	}, [errorMessages, getCurrentTemplateRepresentation, logger, newsletter]);

	const onDownloadPDFClicked = React.useCallback(async () => {
		const htmlDocument = await getCurrentTemplateRepresentation();
		const locationState: ILocationState<any, IEditHtmlNewsletterRequest> = location?.state ?? {};

		exportPdfMutation.mutate({
			templateId: htmlDocument.id,
			impersonationContext: locationState.model?.impersonationContext,
		});
	}, [exportPdfMutation, getCurrentTemplateRepresentation, location?.state]);

	const onDownloadHTMLClicked = React.useCallback(async () => {
		const htmlDocument = await getCurrentTemplateRepresentation();

		openDownloadUrl(
			`data:application/html;charset=utf-8,${encodeURIComponent(htmlDocument.content.document)}`,
			`${newsletter.name}.html`
		);
	}, [getCurrentTemplateRepresentation, newsletter.name]);

	const onSaveClicked = React.useCallback(
		(showToast: boolean) => async () => {
			if (!newsletter.canEdit || isIE11()) {
				return newsletter.template;
			}
			try {
				const template = await getCurrentTemplateRepresentation();

				const savedTemplate = await newsletter.save(template);

				if (template) {
					if (template.name === defaultTemplateName) {
						setSaveModalBehavior({ open: true });
					} else if (showToast) {
						toast.success('Template Saved!');
					}
				} else {
					throw new Error('Cannot read content from editor.');
				}
				reloadTemplate(savedTemplate);

				return savedTemplate;
			} catch (err) {
				const apiError = asApiError(err);
				logger.logApiError('TemplateSave-Error', apiError);
				errorMessages.pushApiError(apiError);
				return null;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[newsletter, getCurrentTemplateRepresentation, editorRef, reloadTemplate]
	);

	const onSendClicked = React.useCallback(async () => {
		const savedTemplate = await onSaveClicked(false)();
		if (savedTemplate) {
			const locationState: ILocationState<any, ICreateCampaignRequest<TemplateOrTemplateFilter>> = {
				model: {
					context: savedTemplate,
					type: 'Template',
				},
			};
			props.fullscreenModal?.history?.push({
				pathname: '/email/campaigns/create/from-template',
				state: locationState,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onSaveClicked]);

	const onSendTestEmailClicked = React.useCallback(async () => {
		try {
			await onSaveClicked(false)();
			await newsletter.sendTestEmail();
			toast.success('A test email has been sent to your email address!');
		} catch (err) {
			const apiError = asApiError(err);
			logger.logApiError('SendTestEmail-Error', apiError);
			errorMessages.pushApiError(apiError);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [newsletter, onSaveClicked]);

	const [editNameModalBehavior, setEditNameModalBehavior] = React.useState<ModalBehaviorProps>({ open: false });
	const [saveAsNewModalBehavior, setSaveAsNewModalBehavior] = React.useState<ModalBehaviorProps>({ open: false });
	const [saveModalBehavior, setSaveModalBehavior] = React.useState<ModalBehaviorProps>({ open: false });

	const onEditNameClicked = React.useCallback(() => {
		setEditNameModalBehavior({ open: true });
	}, []);

	const onSaveAsNewTemplateClicked = React.useCallback(() => {
		setSaveAsNewModalBehavior({ open: true });
	}, []);

	const onShowSaveAsModal = React.useCallback((onSuccess?: ModalBehaviorProps['onSuccess']) => {
		setSaveModalBehavior({ open: true, onSuccess });
	}, []);

	const [autoSaveEnabled, setAutoSaveEnabled] = React.useState(false);

	const toggleAutoSave = React.useCallback(
		(toggleState: boolean) => {
			if (toggleState && newsletter.name === defaultTemplateName) {
				return onShowSaveAsModal(() => {
					setAutoSaveEnabled(true);
				});
			}
			return setAutoSaveEnabled(toggleState);
		},
		[newsletter.name, onShowSaveAsModal]
	);

	const shouldSave = (name: string, newsletterName: string, cancel: boolean) => {
		const trimmedName = name.trim();
		return !!trimmedName && trimmedName !== newsletterName && !cancel;
	};

	const saveTemplateWithKeywords = React.useCallback(
		async (
			name: string,
			keywords: string[] | undefined,
			saveFunction: (template: ITemplate) => Promise<ITemplate>,
			onSuccess?: () => void
		) => {
			const templateToSave: ITemplate = {
				...(await getCurrentTemplateRepresentation()),
				name,
				scope: shareScope,
				keywords,
			};
			const savedTemplate = await saveFunction(templateToSave);
			reloadTemplate(savedTemplate);
			replaceHistoryAfterSavingNewTemplate(savedTemplate);
			onSuccess?.();
		},
		[getCurrentTemplateRepresentation, shareScope, reloadTemplate, replaceHistoryAfterSavingNewTemplate]
	);

	const editNameModalProps = React.useMemo<IModalProps<string>>(() => {
		return {
			isOpen: editNameModalBehavior.open,
			onRequestClose: async (name, cancel) => {
				if (!cancel) {
					const nameChanged = shouldSave(name || '', newsletter.name, false);
					const currentScope = newsletter.template.scope;
					const scopeChanged = shareScope !== currentScope;

					try {
						if (newsletter.id === 'new' || !newsletter.id) {
							if (nameChanged || scopeChanged) {
								const template = await getCurrentTemplateRepresentation();
								if (template) {
									template.name = name;
									template.scope = shareScope;
									const savedTemplate = await newsletter.save(template);
									reloadTemplate(savedTemplate);
									replaceHistoryAfterSavingNewTemplate(savedTemplate);
									toast.success('Template Saved!');
								}
							}
						} else {
							// Handle name update for existing templates
							if (nameChanged) {
								await newsletter.updateName(name);
							}

							// Update scope if it has changed
							if (scopeChanged) {
								await updateScopeMutation.mutateAsync({
									scope: shareScope,
									id: newsletter.template.id,
								});
							}

							// Show appropriate success message
							if (nameChanged && scopeChanged) {
								toast.success('Name and sharing updated!');
							} else if (nameChanged) {
								toast.success('Name updated!');
							} else if (scopeChanged) {
								toast.success('Sharing updated!');
							}
						}
					} catch (err) {
						const apiError = asApiError(err);
						logger.logApiError('EditName-Error', apiError);
						errorMessages.pushApiError(apiError);
					}
				}
				setEditNameModalBehavior({ open: false });
			},
			useDefaultHeader: true,
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		editNameModalBehavior,
		newsletter,
		getCurrentTemplateRepresentation,
		reloadTemplate,
		replaceHistoryAfterSavingNewTemplate,
		shareScope,
		updateScopeMutation,
	]);

	const renderEditTemplateNameInputLabel = () => {
		return <div className={css(styleSheet.editNameLabel)}>Template Name:</div>;
	};

	const externalRef = React.useMemo<IHtmlNewsletterEditorComponent>(() => {
		return {
			getContent: async () => {
				if (!editorRef.current) {
					return null;
				}
				return new Promise<HtmlExport>(resolve => {
					editorRef.current.editor.exportHtml(exported => {
						const htmlWithoutDoctype = HtmlNewsletterViewModel.removeDoctypeFromHtmlStringValue(exported.html);
						resolve({
							...exported,
							// Convert merge field symbols to HTML tokens for backend processing
							html: convertMergeFieldSymbolsToTokens(htmlWithoutDoctype),
						});
					});
				});
			},
		};
	}, [editorRef]);
	React.useEffect(() => {
		onRef?.(externalRef);
		return () => {
			onRef?.(null);
		};
	}, [onRef, externalRef]);

	const headerContext: IHtmlNewsletterEditorHeaderContext = React.useMemo(
		() => ({
			newsletter,
			onEditNameClicked,
			onSaveAsNewTemplateClicked,
			onSaveClicked: onSaveClicked(true),
			onDownloadPDFClicked,
			onDownloadHTMLClicked,
			onAutoSave,
			onSendClicked,
			onSendTestEmailClicked,
			autoSaveEnabled,
			toggleAutoSave,
		}),
		[
			newsletter,
			autoSaveEnabled,
			onAutoSave,
			onSaveClicked,
			onDownloadPDFClicked,
			onDownloadHTMLClicked,
			onSendClicked,
			onEditNameClicked,
			onSaveAsNewTemplateClicked,
			onSendTestEmailClicked,
			toggleAutoSave,
		]
	);

	const bodyContext: IHtmlNewsletterEditorBodyContext = React.useMemo(
		() => ({
			initialDesign: props.newsletter?.design,
			newsletter,
			onEditorLoaded,
		}),
		[newsletter, onEditorLoaded, props.newsletter.design]
	);

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} html-newsletter-editor ${className || ''}`}>
			<HtmlNewsletterEditorHeaderContext.Provider value={headerContext}>
				<HtmlNewsletterEditorBodyContext.Provider value={bodyContext}>
					{children}
				</HtmlNewsletterEditorBodyContext.Provider>
			</HtmlNewsletterEditorHeaderContext.Provider>
			<>
				<TextInputModal
					cta='Save'
					initialValue={newsletter.name}
					inputLabel={renderEditTemplateNameInputLabel()}
					modalProps={editNameModalProps}
					title='Rename Template'
				>
					<ShareTemplateCheckbox shareScope={shareScope} onScopeChange={setShareScope} />
				</TextInputModal>
				<Modal
					isOpen={saveAsNewModalBehavior.open}
					onRequestClose={() => setSaveAsNewModalBehavior({ open: false })}
					useDefaultHeader
					className={css(styleSheet.saveTemplateModal)}
					shouldCloseOnOverlayClick={false}
				>
					{saveAsNewModalBehavior.open ? (
						<SaveTemplateModal
							cta='Save'
							title='Save as New Template'
							shareScope={shareScope}
							onScopeChange={setShareScope}
							enableKeywords
							impersonationContext={
								(location?.state as ILocationState<any, IEditHtmlNewsletterRequest>)?.model?.impersonationContext
							}
							onSave={async (name, keywords) => {
								if (name) {
									try {
										await saveTemplateWithKeywords(
											name,
											keywords,
											newsletter.saveAsNewTemplate,
											saveAsNewModalBehavior.onSuccess
										);
										toast.success('New Template Saved!');
									} catch (err) {
										logger.logApiError('SaveAsNewTemplate-Error', asApiError(err));
									}
									setSaveAsNewModalBehavior({ open: false });
								}
							}}
							onCancel={() => setSaveAsNewModalBehavior({ open: false })}
						/>
					) : null}
				</Modal>
				<Modal
					isOpen={saveModalBehavior.open}
					onRequestClose={() => setSaveModalBehavior({ open: false })}
					useDefaultHeader
					className={css(styleSheet.saveTemplateModal)}
					shouldCloseOnOverlayClick={false}
				>
					{saveModalBehavior.open ? (
						<SaveTemplateModal
							cta='Save'
							title='Save Template'
							shareScope={shareScope}
							onScopeChange={setShareScope}
							enableKeywords
							impersonationContext={
								(location?.state as ILocationState<any, IEditHtmlNewsletterRequest>)?.model?.impersonationContext
							}
							onSave={async (name, keywords) => {
								if (name) {
									try {
										await saveTemplateWithKeywords(name, keywords, newsletter.save, saveModalBehavior.onSuccess);
										toast.success('Template Saved!');
									} catch (err) {
										logger.logApiError('SaveNewTemplate-Error', asApiError(err));
									}
									setSaveModalBehavior({ open: false });
								}
							}}
							onCancel={() => setSaveModalBehavior({ open: false })}
						/>
					) : null}
				</Modal>

				<ExportConfirmationModalV2
					title='Export to PDF'
					documentKind='PDF'
					mutationResult={exportPdfMutation}
					modalProps={{
						isOpen: !exportPdfMutation.isIdle,
						onRequestClose: () => {
							exportPdfMutation.reset();
						},
					}}
				/>
			</>
		</div>
	);
};

const HtmlNewsletterEditorAsObserver = observer(HtmlNewsletterEditorSFC);
export const HtmlNewsletterEditor = inject(
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey
)(HtmlNewsletterEditorAsObserver);

const CustomJsUrl = new URL('./customJs.unlayer.js', import.meta.url).href;

export const HtmlNewsletterEditorDefaultEditorOptions = {
	appearance: {
		panels: {
			tools: {
				dock: 'right',
			},
		},
		theme: 'light',
	},
	customCSS: [unlayerCustomCss],
	customJS: [CustomJsUrl.replace('./', `${window.location.origin}/`)],
	features: {
		smartMergeTags: true,
		userUploads: false,
		stockImages: false,
	},
	fonts: {
		customFonts: getEmailEditorFonts({ useExternal: false }),
		showDefaultFonts: false,
	},
	mergeTags: {
		contact: {
			mergeTags: DefaultContactPlaceholders.reduce<Record<string, SimpleMergeTag>>((res, x) => {
				res[x.value] = {
					name: x.text
						.split(' ')
						.map(s => `${s.charAt(0).toLocaleUpperCase()}${s.substring(1)}`)
						.join(' '),
					// Use symbol format - Unlayer escapes HTML, so we insert symbols and convert on export
					value: x.symbol,
				};
				return res;
			}, {}),
			name: 'Contact',
		},
		sender: {
			mergeTags: {
				[SenderFirstNamePlaceholder.value]: {
					name: 'First Name',
					value: SenderFirstNamePlaceholder.symbol,
				},
				[SenderFullNamePlaceholder.value]: {
					name: 'Full Name',
					value: SenderFullNamePlaceholder.symbol,
				},
				[SenderEmailPlaceholder.value]: {
					name: 'Email',
					value: SenderEmailPlaceholder.symbol,
				},
				[SenderPhonePlaceholder.value]: {
					name: 'Phone Number',
					value: SenderPhonePlaceholder.symbol,
				},
			},
			name: 'Sender',
		},
	},
	projectId: 11037,
	version: 'latest',
	tools: {
		social: {
			enabled: true,
		},
	},
} as const satisfies UnlayerOptions;
